import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { Noke } from "utilities/iconsNew";
import { userSignOut } from "actions/Auth";
import UserInitialsAvatar from "components/UserInitialsAvatar";
import Button from "components/ButtonIconOnly";
import IconWithBackground from "components/IconWithBackground";
import Icon from "components/Icon";
import Popout from "components/Popout";
import Tooltip from "components/Tooltip";

const Sidebar = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const authUser = useSelector((state) => state.auth.authUser);
  const theme = useSelector((state) => state.app.theme);
  const [isHelpPopoutOpen, setIsHelpPopoutOpen] = useState(false);
  const [isUserPopoutOpen, setIsUserPopoutOpen] = useState(false);

  const popoutAnchorRef = useRef(null);
  const isFacilityView = location.pathname !== "/app/facilities/allFacilities" && location.pathname !== "/app/facilities/myFacilities";

  const facilityMenu = [
    { name: "Details", icon: <Icon name="dashboard" size={24} />, path: "/reports" },
    { name: "UnitsUsers", icon: <Icon name="users" size={24} />, path: "/entries" },
    { name: "Activity", icon: <Icon name="activity" size={24} />, path: "/network" },
    { name: "Settings", icon: <Icon name="settings" size={24} />, path: "/settings" },
  ];

  return (
    <aside className="app-new-sidebar h-screen min-w-[114px] flex flex-col justify-between bg-[var(--color-glass-dark)] p-6 pt-6 pb-10">
      <div className="flex justify-center items-center h-[52px]">
        {theme && theme.menuLogo
        ?
        <img
          width="66"
          src={theme.menuLogo}
        />
        :
        <Noke/>}
      </div>
      <nav className="flex flex-col gap-7">
        {isFacilityView && (
          <>
            {facilityMenu.map((item, index) => (
              <button
                id={`${item.name}-sidebar-button`}
                data-cy={`${item.name}-sidebar-button`}
                key={index}
                className={`${index === 0 ? "bg-white" : "bg-[var(--color-glass-x-light)]"} flex items-center justify-center w-full p-5 rounded-[100px]`}
              >
                {item.icon}
              </button>
            ))}
          </>
        )}
      </nav>
      <Popout 
        isOpen={isHelpPopoutOpen} 
        onClose={() => setIsHelpPopoutOpen(false)} 
        anchorRef={popoutAnchorRef} 
        positioning="right" 
        title={intl.formatMessage({id: "help.supportCenter"})}
      >
        <div className="w-120 flex flex-col gap-8 mt-4">
          <button
            id="sidebar-live-chat-button"
            data-cy="sidebar-live-chat-button"
            className="flex items-center"
            onClick={() => {window.HubSpotConversations.widget.open(); setIsHelpPopoutOpen(false)}}
          >
            <IconWithBackground
              iconName="chat"
              backgroundColor="#F5F5F5"
              className="text-[var(--color-gray-300)]"
            />
            <h6 className="tw-heading-6 pl-4">{intl.formatMessage({id: "help.liveChat"})}</h6>
          </button>
          <button
            id="sidebar-general-help-button"
            data-cy="sidebar-general-help-button"
            className="flex items-center"
            onClick={() => {window.open("https://www.janusintl.com/knowledge", "_blank", "noopener,noreferrer"); setIsHelpPopoutOpen(false)}}  
          >
            <IconWithBackground
              iconName="info"
              backgroundColor="#F5F5F5"
              className="text-[var(--color-gray-300)]"
            />
            <h6 className="tw-heading-6 pl-4">{intl.formatMessage({id: "help.generalHelp"})}</h6>
          </button>
          <button
            id="sidebar-resource-center-button"
            data-cy="sidebar-resource-center-button"
            className="flex items-center"
            onClick={() => {window.pendo.BuildingBlocks.BuildingBlockResourceCenter.getResourceCenter().show(); setIsHelpPopoutOpen(false)}}
          >
            <IconWithBackground
              iconName="openBook"
              backgroundColor="#F5F5F5"
              className="text-[var(--color-gray-300)]"
            />
            <h6 className="tw-heading-6 pl-4">{intl.formatMessage({id: "help.resourceCenter"})}</h6>
          </button>
        </div>
      </Popout>
      <Popout 
        isOpen={isUserPopoutOpen} 
        onClose={() => setIsUserPopoutOpen(false)} 
        anchorRef={popoutAnchorRef} 
        positioning="right" 
        header={false}
      >
        <div className="w-60">
          {/* <button
            id="sidebar-manage-profile-button"
            data-cy="sidebar-manage-profile-button"
            onClick={() => {}}
            className="tw-paragraph-reg block text-left px-4 py-2 mt-6 hover:bg-[var(--color-gray-100)] rounded-[8px]"
          >
            {intl.formatMessage({id: "user.manageProfile"})}
          </button> */}
          <button
            id="sidebar-logout-button"
            data-cy="sidebar-logout-button"
            onClick={() => dispatch(userSignOut())}
            // take out the mt-6 below when you add the above button back in
            className="tw-paragraph-reg block text-left mt-6 px-4 py-2 hover:bg-[var(--color-gray-100)] rounded-[8px]"
          >
            {intl.formatMessage({id: "comp.button.logout"})}
          </button>
        </div>
      </Popout>
      {/* Bottom Icons */}
      <div className="flex flex-col items-center gap-4">
        {/* <Button
          icon="notification"
          type="uncontained"
          id="sidebar-notification-button"
          data-cy="sidebar-notification-button"
          onClick={() => {}}
        /> */}
        <Button
          ref={popoutAnchorRef}
          icon="help"
          type="uncontained"
          id="sidebar-help-button"
          data-cy="sidebar-help-button"
          onClick={() => setIsHelpPopoutOpen(true)}
        />
        <Tooltip position="right" anchorRef={popoutAnchorRef} text={intl.formatMessage({id: "help.supportCenter"})} />
        <UserInitialsAvatar
          id="sidebar-user-button"
          data-cy="sidebar-user-button"
          firstName={authUser.firstName}
          lastName={authUser.lastName}
          onClick={() => setIsUserPopoutOpen(true)}
        />
      </div>
    </aside>
  );
};

export default Sidebar;
